import { format } from "date-fns";
import { Instrument } from "./instrument_type";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

interface Props {
  data: Instrument[];
  onSelect?: (device: Instrument) => void;
  selectedId?: number | null;
}

const SensorTable = ({ data, onSelect, selectedId }: Props) => {
  const sensors = data.filter((d) => d.deviceType === "Sensor");

  if (sensors.length === 0) return null;

  return (
    <Table
      wrapperClassName="table-responsive rounded border"
      className="table table-hover table-bordered align-middle text-center mb-0"
    >
      <TableHeader className="bg-light-primary">
        <TableRow>
          <TableHead
            rowSpan={2}
            className="text-center align-middle border-end"
          >
            Instrument
          </TableHead>

          <TableHead
            colSpan={3}
            className="text-center border-start border-end"
          >
            Readings
          </TableHead>

          <TableHead
            colSpan={3}
            className="text-center border-start border-end"
          >
            Details
          </TableHead>

          <TableHead
            colSpan={1}
            className="text-center border-start border-end"
          >
            Status
          </TableHead>

          <TableHead
            rowSpan={2}
            className="text-center align-middle border-start"
          >
            Alert
          </TableHead>

          <TableHead
            rowSpan={2}
            className="text-center align-middle border-start"
          >
            Action
          </TableHead>
        </TableRow>

        <TableRow>
          <TableHead className="text-center border-start">
            Moisture (%)
          </TableHead>

          <TableHead className="text-center">
            Temp (°C)
          </TableHead>

          <TableHead className="text-center border-end">
            Battery (%)
          </TableHead>

          <TableHead className="text-center">
            State
          </TableHead>

          <TableHead className="text-center">
            Dry SP
          </TableHead>

          <TableHead className="text-center border-end">
            Wet SP
          </TableHead>

          <TableHead className="text-center border-end">
            Type
          </TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {sensors.map((device) => {
          const moisture = device.moisturePercentage ?? 0;
          const battery = device.battery_Percent ?? 0;

          const moistureState =
            device.dry === 1 && device.wet === 0
              ? "Dry"
              : device.dry === 0 && device.wet === 1
              ? "Wet"
              : "Normal";

          const hasAlert =
            moistureState !== "Normal" ||
            !!device.highTemperatureAlarm ||
            !!device.lowBatteryAlarm;

          return (
            <TableRow
              key={device.id}
              className={`cursor-pointer ${
                selectedId === device.id ? "selected-card" : ""
              }`}
              onClick={() => onSelect?.(device)}
            >
              <TableCell className="text-center text-nowrap border-end">
                <p className="fw-medium mb-0">
                  {device.instrumentName ?? "-"}
                </p>

                <p className="small text-muted mb-0">
                  {device.dateTime
                    ? format(
                        new Date(device.dateTime),
                        "yyyy-MM-dd HH:mm:ss",
                      )
                    : "-"}
                </p>
              </TableCell>

              <TableCell className="text-center border-start">
                {moisture}%
              </TableCell>

              <TableCell className="text-center">
                {device.temperature ?? "-"}
              </TableCell>

              <TableCell className="text-center border-end">
                {battery}%
              </TableCell>

              <TableCell className="text-center">
                <span
                  className={
                    moistureState === "Dry"
                      ? "text-warning fw-medium"
                      : moistureState === "Wet"
                      ? "text-primary fw-medium"
                      : "text-success fw-medium"
                  }
                >
                  {moistureState}
                </span>
              </TableCell>

              <TableCell className="text-center">
                {device.dry_SetPoint ?? "-"}
              </TableCell>

              <TableCell className="text-center border-end">
                {device.wet_SetPoint ?? "-"}
              </TableCell>

              <TableCell className="text-center border-end">
                Sensor
              </TableCell>

              <TableCell className="text-center border-start">
                <span
                  className={`device-alert-indicator ${
                    hasAlert ? "alarm" : "ok"
                  }`}
                />
              </TableCell>

              <TableCell className="text-center border-start">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect?.(device);
                  }}
                >
                  Details
                </button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default SensorTable;